import { useState, useEffect, useCallback, useRef } from 'react';
import { networkLogger } from '@/shared/lib/logger';

/**
 * Connection quality levels for adaptive UI and data fetching strategies.
 */
export type ConnectionQuality = 'fast' | 'moderate' | 'slow' | 'offline';

/**
 * Comprehensive network status information.
 */
export interface NetworkStatus {
  isOnline: boolean;
  quality: ConnectionQuality;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NetworkMonitorCallbacks {
  onOnline?: () => void;
  onOffline?: () => void;
  onQualityChange?: (quality: ConnectionQuality) => void;
}

/**
 * Hook for monitoring network connectivity and connection quality.
 * Provides adaptive strategies for offline-first applications.
 * 
 * @param callbacks - Optional callbacks for network state changes
 * @returns Network status and utility methods
 */
export const useNetworkMonitor = (callbacks?: NetworkMonitorCallbacks) => {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    quality: 'moderate',
  });

  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const lastOnlineRef = useRef(Date.now());

  const getConnectionQuality = useCallback((): ConnectionQuality => {
    if (!navigator.onLine) return 'offline';

    // Check Network Information API if available
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;

    if (connection) {
      const effectiveType = connection.effectiveType;
      const downlink = connection.downlink; // Mbps

      // Quality based on effective type and downlink
      if (effectiveType === '4g' && downlink > 5) return 'fast';
      if (effectiveType === '4g' || (effectiveType === '3g' && downlink > 1)) return 'moderate';
      if (effectiveType === '3g' || effectiveType === '2g') return 'slow';
    }

    // Fallback: assume moderate if no info available
    return 'moderate';
  }, []);

  const getNetworkInfo = useCallback((): Partial<NetworkStatus> => {
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;

    if (connection) {
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData,
      };
    }

    return {};
  }, []);

  const updateStatus = useCallback(() => {
    const isOnline = navigator.onLine;
    const quality = getConnectionQuality();
    const networkInfo = getNetworkInfo();

    setStatus({
      isOnline,
      quality,
      ...networkInfo,
    });

    return { isOnline, quality };
  }, [getConnectionQuality, getNetworkInfo]);

  const handleOnline = useCallback(() => {
    networkLogger.info('Connection restored');
    
    const { quality } = updateStatus();
    callbacks?.onOnline?.();
    callbacks?.onQualityChange?.(quality);

    // Calculate offline duration
    const offlineDuration = Date.now() - lastOnlineRef.current;
    networkLogger.debug(`Was offline for ${Math.round(offlineDuration / 1000)}s`);

    // Reset reconnect attempts
    reconnectAttemptsRef.current = 0;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, [updateStatus, callbacks]);

  const handleOffline = useCallback(() => {
    networkLogger.info('Connection lost');
    lastOnlineRef.current = Date.now();
    
    updateStatus();
    callbacks?.onOffline?.();

    // Start reconnection attempts with exponential backoff
    attemptReconnect();
  }, [updateStatus, callbacks]);

  const attemptReconnect = useCallback(() => {
    reconnectAttemptsRef.current += 1;
    
    // Exponential backoff: 2^n seconds, max 60 seconds
    const delay = Math.min(Math.pow(2, reconnectAttemptsRef.current) * 1000, 60000);
    
    networkLogger.debug(`Reconnect attempt ${reconnectAttemptsRef.current} in ${delay / 1000}s`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!navigator.onLine) {
        attemptReconnect();
      }
    }, delay);
  }, []);

  const handleConnectionChange = useCallback(() => {
    const { quality } = updateStatus();
    callbacks?.onQualityChange?.(quality);
  }, [updateStatus, callbacks]);

  useEffect(() => {
    // Initial status
    updateStatus();

    // Listen to online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen to connection changes
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [updateStatus, handleOnline, handleOffline, handleConnectionChange]);

  const getConnectionStrength = (): number => {
    switch (status.quality) {
      case 'fast': return 100;
      case 'moderate': return 60;
      case 'slow': return 30;
      case 'offline': return 0;
      default: return 50;
    }
  };

  const shouldDeferImages = (): boolean => {
    return status.quality === 'slow' || status.saveData === true;
  };

  const shouldReduceQuality = (): boolean => {
    return status.quality === 'slow' || status.quality === 'moderate';
  };

  return {
    status,
    isOnline: status.isOnline,
    quality: status.quality,
    connectionStrength: getConnectionStrength(),
    shouldDeferImages: shouldDeferImages(),
    shouldReduceQuality: shouldReduceQuality(),
    reconnectAttempts: reconnectAttemptsRef.current,
  };
};
